import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import pb from '../lib/pocketbase';
import TiptapEditor from './TiptapEditor';
import BlockListEditor from './BlockListEditor';
import { useServicesData } from '../context/ServicesContext';
// DnD Imports
import { DndContext, closestCenter, KeyboardSensor, PointerSensor, useSensor, useSensors } from '@dnd-kit/core';
import { arrayMove, SortableContext, sortableKeyboardCoordinates, verticalListSortingStrategy, useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { FiPlus, FiEdit2, FiTrash2, FiSave, FiX, FiEye, FiEyeOff, FiMenu, FiChevronUp, FiChevronDown, FiFolder } from 'react-icons/fi';
import './Admin.css';
import '../RecruitmentComponents.css';

const ServicesLoader = () => {
    const [msgIdx, setMsgIdx] = useState(0);
    const messages = ["Analyse du back-office...", "Chargement de la structure...", "Synchronisation PocketBase...", "Préparation de l'interface..."];

    useEffect(() => {
        const timer = setInterval(() => {
            setMsgIdx(prev => (prev + 1) % messages.length);
        }, 2200);
        return () => clearInterval(timer);
    }, []);

    return (
        <div className="recrutement-loader-container" style={{ minHeight: '80vh' }}>
            <div className="loader-visual">
                <div className="loader-orbit orbit-1"></div>
                <div className="loader-orbit orbit-2"></div>
                <div className="loader-orbit orbit-3"></div>
                <div className="loader-logo-wrapper">
                    <img src="/images/logo.png" alt="Version Net" className="loader-logo" />
                </div>
            </div>
            <div className="loader-message-wrapper">
                <p className="loader-text" style={{ color: 'var(--admin-text)' }}>{messages[msgIdx]}</p>
                <div className="loader-progress-bar">
                    <div className="loader-progress-fill"></div>
                </div>
            </div>
        </div>
    );
};

const SortableModalCategory = ({ cat, index }) => {
    const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: cat.id });
    const style = {
        transform: CSS.Transform.toString(transform),
        transition,
        opacity: isDragging ? 0.5 : 1,
        background: 'var(--admin-card-bg)',
        border: '1px solid var(--admin-border)',
        borderRadius: '8px',
        padding: '1rem',
        marginBottom: '0.5rem',
        display: 'flex',
        alignItems: 'center',
        gap: '1rem',
        cursor: 'grab'
    };

    return (
        <div ref={setNodeRef} style={style} {...attributes} {...listeners}>
            <div style={{ color: 'var(--admin-text-muted)' }}>
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="9" cy="5" r="1" /><circle cx="9" cy="12" r="1" /><circle cx="9" cy="19" r="1" /><circle cx="15" cy="5" r="1" /><circle cx="15" cy="12" r="1" /><circle cx="15" cy="19" r="1" /></svg>
            </div>
            <div style={{ fontWeight: 600, flex: 1 }}>{cat.title}</div>
            <div style={{ fontSize: '0.75rem', color: 'var(--admin-text-muted)', background: 'var(--admin-hover-bg)', padding: '2px 8px', borderRadius: '4px' }}>
                Ordre : {index + 1}
            </div>
        </div>
    );
};

function SortableSection({ sec, selectedSection, onSelect, deleteSection, onTogglePublish }) {
    const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: sec.id });

    const style = {
        transform: CSS.Transform.toString(transform),
        transition,
        opacity: isDragging ? 0.5 : 1,
        zIndex: isDragging ? 1000 : 'auto',
    };

    return (
        <div
            ref={setNodeRef}
            style={style}
            className={`section-admin-item ${selectedSection?.id === sec.id ? 'active' : ''} ${sec.is_published === false ? 'is-draft' : ''}`}
            onClick={() => onSelect(sec)}
        >
            <div
                {...attributes}
                {...listeners}
                className="section-drag-handle"
                onClick={e => e.stopPropagation()}
            >
                <FiMenu />
            </div>

            <div className="section-icon-preview">
                <FiFolder />
            </div>

            <div className="section-info">
                <div className="section-meta">
                    <span className="section-badge">Service</span>
                    {sec.is_published === false && (
                        <span className="draft-badge"><FiEyeOff /> Dépublié</span>
                    )}
                </div>
                <h4 className="section-name">{sec.title}</h4>
                <p className="section-excerpt">#{sec.slug}</p>
            </div>

            <div className="section-item-actions">
                <button
                    type="button"
                    className={`btn-icon-round ${sec.is_published === false ? 'is-draft-btn' : 'is-published-btn'}`}
                    onClick={(e) => { e.stopPropagation(); onTogglePublish(sec); }}
                    title={sec.is_published === false ? "Publier" : "Dépublier"}
                >
                    {sec.is_published === false ? <FiEyeOff /> : <FiEye />}
                </button>
                <button
                    type="button"
                    className="btn-icon-round"
                    onClick={(e) => { e.stopPropagation(); onSelect(sec); }}
                    title="Modifier"
                >
                    <FiEdit2 />
                </button>
                <button
                    type="button"
                    className="btn-icon-round delete"
                    onClick={(e) => { e.stopPropagation(); deleteSection(sec.id); }}
                    title="Supprimer"
                >
                    <FiTrash2 />
                </button>
            </div>
        </div>
    );
}

function SortableCategory({ cat, catIdx, categoriesCount, categories, sections, selectedCategory, handleSelectCategory, moveCategory, createSection, deleteCategory, selectedSection, handleSelectSection, deleteSection, moveSection, onSectionDragEnd, sensors, confirmNavigation, toggleSectionStatus }) {
    const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: cat.id });

    const catSections = sections
        .filter(s => (typeof s.category === 'object' ? s.category?.id : s.category) === cat.id)
        .sort((a, b) => (a.order || 0) - (b.order || 0));

    const style = {
        transform: CSS.Transform.toString(transform),
        transition,
        opacity: isDragging ? 0.5 : 1,
        zIndex: isDragging ? 900 : 'auto',
    };

    return (
        <div ref={setNodeRef} className="admin-structure-group" style={{
            ...style,
            background: 'var(--admin-hover-bg)',
            border: '1px solid var(--admin-border)',
            borderRadius: '8px',
            padding: '1.25rem',
            marginBottom: '1.5rem'
        }}>
            {/* Category Header */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.25rem' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                    <div {...attributes} {...listeners} style={{ cursor: 'grab', touchAction: 'none', color: 'var(--admin-text-muted)', display: 'flex' }}>
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="9" cy="5" r="1" /><circle cx="9" cy="12" r="1" /><circle cx="9" cy="19" r="1" /><circle cx="15" cy="5" r="1" /><circle cx="15" cy="12" r="1" /><circle cx="15" cy="19" r="1" /></svg>
                    </div>
                    <div>
                        <h3
                            onClick={() => confirmNavigation(() => handleSelectCategory(cat))}
                            style={{ margin: 0, cursor: 'pointer', textDecoration: selectedCategory?.id === cat.id ? 'underline' : 'none', color: 'var(--admin-text)', fontSize: '1.1rem' }}
                            title="Cliquez pour éditer"
                        >
                            📁 {cat.title}
                        </h3>
                        <span style={{ fontSize: '0.8rem', color: 'var(--admin-text-muted)' }}>#{cat.slug}</span>
                    </div>
                </div>
                <div style={{ display: 'flex', gap: '0.5rem' }}>
                    <button type="button" className="btn btn-sm btn-secondary" onClick={() => createSection(cat.id)}>+ Ajouter service</button>
                    <button type="button" className="btn-icon delete" onClick={() => deleteCategory(cat.id)}>🗑️</button>
                </div>
            </div>

            {/* Sections List with its own DnD Context */}
            <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={(e) => onSectionDragEnd(e, cat.id)}>
                <SortableContext items={catSections.map(s => s.id)} strategy={verticalListSortingStrategy}>
                    <div className="structure-list" style={{ display: 'flex', flexDirection: 'column' }}>
                        {catSections.length === 0 && <div className="empty-state-mini" style={{ padding: '1rem', fontStyle: 'italic', color: 'var(--admin-text-muted)', textAlign: 'center', border: '1px dashed var(--admin-border)', borderRadius: '6px' }}>Aucune section dans ce groupe.</div>}

                        {catSections.map((sec, sIdx) => (
                            <SortableSection
                                key={sec.id}
                                sec={sec}
                                selectedSection={selectedSection}
                                onSelect={(s) => confirmNavigation(() => handleSelectSection(s))}
                                deleteSection={deleteSection}
                                onTogglePublish={toggleSectionStatus}
                            />
                        ))}
                    </div>
                </SortableContext>
            </DndContext>
        </div>
    );
}

export default function AdminServicesManager() {
    const { categories, sections, hero: contextHero, loading: contextLoading, refresh } = useServicesData();
    const [selectedSection, setSelectedSection] = useState(null); // The section being edited
    const [selectedCategory, setSelectedCategory] = useState(null); // The category being edited (if any)
    const [isSaving, setIsSaving] = useState(false);
    const [isDirty, setIsDirty] = useState(false);

    const sensors = useSensors(
        useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
        useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
    );

    const onCategoryDragEnd = async (event) => {
        const { active, over } = event;
        if (!over || active.id === over.id) return;

        const oldIndex = categories.findIndex(c => c.id === active.id);
        const newIndex = categories.findIndex(c => c.id === over.id);
        const newCategories = arrayMove(categories, oldIndex, newIndex);

        try {
            await Promise.all(newCategories.map((c, idx) =>
                pb.collection('services_categories').update(c.id, { order: idx })
            ));
            refresh();
        } catch (err) {
            console.error("Error updating service categories order", err);
        }
    };

    const onSectionDragEnd = async (event, catId) => {
        const { active, over } = event;
        if (!over || active.id === over.id) return;

        const catSections = sections.filter(s => (typeof s.category === 'object' ? s.category?.id : s.category) === catId)
            .sort((a, b) => (a.order || 0) - (b.order || 0));

        const oldIndex = catSections.findIndex(s => s.id === active.id);
        const newIndex = catSections.findIndex(s => s.id === over.id);
        const newCatSections = arrayMove(catSections, oldIndex, newIndex);

        try {
            await Promise.all(newCatSections.map((s, idx) =>
                pb.collection('services_sections').update(s.id, { order: idx })
            ));
            refresh();
        } catch (err) {
            console.error("Error updating service sections order", err);
        }
    };

    const toggleSectionStatus = async (sec) => {
        try {
            await pb.collection('services_sections').update(sec.id, { is_published: !sec.is_published });
            showToast(sec.is_published ? "Mise hors-ligne effectuée" : "Mise en ligne effectuée");
            await refresh();
        } catch (err) {
            console.error("Error toggling section status", err);
            showToast("Erreur lors du changement de statut", "error");
        }
    };

    const [toast, setToast] = useState({ show: false, message: '', type: 'success' });
    const [showSaveConfirm, setShowSaveConfirm] = useState(false);
    const [pendingSave, setPendingSave] = useState(null);
    const [confirmModal, setConfirmModal] = useState({ show: false, title: '', message: '', onConfirm: null, isDanger: false });
    const [showNewCatModal, setShowNewCatModal] = useState(false);
    const [showManageCatsModal, setShowManageCatsModal] = useState(false);
    const [newCatTitle, setNewCatTitle] = useState('');

    const showToast = (message, type = 'success') => {
        setToast({ show: true, message, type });
        setTimeout(() => setToast(prev => ({ ...prev, show: false })), 3000);
    };

    // Edit State for Section
    const [editData, setEditData] = useState({
        title: '',
        slug: '',
        content: '',
        blocks: [], // Array of block objects
        category: '',
        order: 0,
        is_published: true
    });

    // Edit State for Category
    const [catEditData, setCatEditData] = useState({
        title: '',
        slug: '',
        order: 0
    });

    const slugify = (text) => {
        return text
            .toString()
            .toLowerCase()
            .normalize('NFD') // separate accents
            .replace(/[\u0300-\u036f]/g, '') // remove accents
            .replace(/[^a-z0-9]+/g, '-') // replace non-alphanumeric with hyphens
            .replace(/(^-|-$)/g, ''); // remove leading/trailing hyphens
    };

    const handleSaveError = (err) => {
        console.error(err);
        if (err.status === 400 && err.data?.data?.slug) {
            showToast("Erreur : Ce slug est déjà utilisé.", "error");
        } else {
            showToast("Une erreur est survenue lors de la sauvegarde.", "error");
        }
    };


    // Unsaved changes warning
    useEffect(() => {
        const handleBeforeUnload = (e) => {
            if (isDirty) {
                e.preventDefault();
                e.returnValue = ''; // Standard for showing the confirm dialog
            }
        };
        window.addEventListener('beforeunload', handleBeforeUnload);
        return () => window.removeEventListener('beforeunload', handleBeforeUnload);
    }, [isDirty]);

    const [heroData, setHeroData] = useState({
        title: 'Services',
        subtitle: 'Un accompagnement de bout en bout...',
        slider_title: '',
        slider_subtitle: '',
        buttons: []
    });
    const [heroId, setHeroId] = useState(null);

    // Initialize Hero Data from Context
    useEffect(() => {
        if (contextHero) {
            setHeroData({
                title: contextHero.title || 'Services',
                subtitle: contextHero.subtitle || '',
                slider_title: contextHero.slider_title || '',
                slider_subtitle: contextHero.slider_subtitle || '',
                buttons: contextHero.buttons || []
            });
            // Fetch ID silently for updates
            pb.collection('services_hero').getFullList({ sort: '-created', max: 1 }).then(list => {
                if (list.length > 0) setHeroId(list[0].id);
            });
        }
    }, [contextHero]);


    const saveHero = (e) => {
        e.preventDefault();
        setPendingSave(() => executeSaveHero);
        setShowSaveConfirm(true);
    };

    const executeSaveHero = async () => {
        setIsSaving(true);
        try {
            if (heroId) {
                await pb.collection('services_hero').update(heroId, heroData);
            } else {
                const newHero = await pb.collection('services_hero').create(heroData);
                setHeroId(newHero.id);
            }
            showToast("En-tête mis à jour !");
        } catch (err) {
            console.error(err);
            showToast("Erreur sauvegarde en-tête.", "error");
        } finally {
            setIsSaving(false);
            setIsDirty(false);
            setShowSaveConfirm(false);
        }
    };

    const updateButton = (index, field, value) => {
        const newButtons = [...heroData.buttons];
        newButtons[index][field] = value;
        setHeroData({ ...heroData, buttons: newButtons });
        setIsDirty(true);
    };

    const addButton = () => {
        setHeroData({
            ...heroData,
            buttons: [...heroData.buttons, { label: 'Bouton', url: '/', style: 'btn-secondary' }]
        });
        setIsDirty(true);
    };

    const removeButton = (index) => {
        const newButtons = heroData.buttons.filter((_, i) => i !== index);
        setHeroData({ ...heroData, buttons: newButtons });
        setIsDirty(true);
    };

    // --- SELECTION HANDLERS ---

    const handleSelectSection = (section) => {
        setSelectedSection(section);
        setSelectedCategory(null); // Deselect category editor
        setEditData({
            title: section.title,
            slug: section.slug,
            content: section.content,
            order: section.order || 0,
            blocks: section.blocks || [],
            is_published: section.is_published !== false
        });
        setIsDirty(false);
        setTimeout(() => document.getElementById('admin-editor-area')?.scrollIntoView({ behavior: 'smooth' }), 100);
    };

    const confirmNavigation = (nextAction) => {
        if (isDirty) {
            setConfirmModal({
                show: true,
                title: "Modifications non enregistrées",
                message: "Vous avez des modifications en cours. Si vous changez de page maintenant, elles seront définitivement perdues.",
                isDanger: true,
                onConfirm: () => {
                    setConfirmModal(prev => ({ ...prev, show: false }));
                    nextAction();
                }
            });
        } else {
            nextAction();
        }
    };

    const handleSelectCategory = (category) => {
        setSelectedCategory(category);
        setSelectedSection(null); // Deselect section editor
        setCatEditData({
            title: category.title,
            slug: category.slug,
            order: category.order || 0
        });
        setIsDirty(false); // Reset dirty on selection
        setTimeout(() => document.getElementById('admin-editor-area')?.scrollIntoView({ behavior: 'smooth' }), 100);
    };

    // --- CATEGORY ACTIONS ---

    const createCategory = () => {
        setNewCatTitle('');
        setShowNewCatModal(true);
    };

    const handleConfirmCreateCategory = async () => {
        if (!newCatTitle.trim()) return;

        const maxOrder = categories.reduce((max, c) => Math.max(max, c.order || 0), 0);
        const slug = slugify(newCatTitle);

        try {
            setIsSaving(true);
            const newCat = await pb.collection('services_categories').create({
                title: newCatTitle,
                slug,
                order: maxOrder + 1
            });
            await refresh();
            handleSelectCategory(newCat);
            showToast("Groupe créé !");
            setShowNewCatModal(false);
        } catch (err) {
            console.error(err);
            showToast("Erreur création groupe", "error");
        } finally {
            setIsSaving(false);
        }
    };

    const deleteCategory = async (id) => {
        setConfirmModal({
            show: true,
            title: "Supprimer ce groupe ?",
            message: "Action irréversible : cela supprimera également TOUTES les sections à l'intérieur (Services).",
            isDanger: true,
            onConfirm: async () => {
                try {
                    setIsSaving(true);
                    // 1. Delete associated sections (Cascade)
                    const relatedSections = sections.filter(s =>
                        (typeof s.category === 'object' ? s.category?.id : s.category) === id
                    );
                    if (relatedSections.length > 0) {
                        await Promise.all(relatedSections.map(s => pb.collection('services_sections').delete(s.id)));
                    }

                    // 2. Delete Category
                    await pb.collection('services_categories').delete(id);
                    
                    if (selectedCategory?.id === id) setSelectedCategory(null);
                    await refresh();
                    showToast("Groupe et services supprimés !");
                } catch (err) {
                    console.error("Delete failed:", err);
                    showToast("Erreur suppression (Vérifiez les droits API)", "error");
                } finally {
                    setIsSaving(false);
                    setConfirmModal(prev => ({ ...prev, show: false }));
                }
            }
        });
    };

    const saveCategory = (e) => {
        e.preventDefault();
        setPendingSave(() => executeSaveCategory);
        setShowSaveConfirm(true);
    };

    const executeSaveCategory = async () => {
        if (!selectedCategory) return;
        setIsSaving(true);
        try {
            await pb.collection('services_categories').update(selectedCategory.id, catEditData);
            await refresh();
            showToast("Groupe mis à jour !");
        } catch (err) {
            handleSaveError(err);
        } finally {
            setIsSaving(false);
            setIsDirty(false);
            setShowSaveConfirm(false);
        }
    };

    // --- SECTION ACTIONS ---

    const createSection = async (categoryId) => {
        const cat = categories.find(c => c.id === categoryId);

        setConfirmModal({
            show: true,
            title: "Créer un nouveau service ?",
            message: `Voulez-vous ajouter un nouveau service dans le groupe "${cat?.title}" ?`,
            isDanger: false,
            onConfirm: async () => {
                const catSections = sections.filter(s => (typeof s.category === 'object' ? s.category?.id : s.category) === categoryId);
                const maxOrder = catSections.reduce((max, s) => Math.max(max, s.order || 0), 0);

                try {
                    setIsSaving(true);
                    const newSec = await pb.collection('services_sections').create({
                        title: 'Nouveau Service',
                        slug: `service-${Date.now()}`,
                        category: categoryId,
                        content: '<p>Contenu...</p>',
                        order: maxOrder + 1,
                        is_published: true
                    });
                    await refresh();
                    handleSelectSection(newSec);
                    showToast("Service créé !");
                } catch (err) {
                    console.error(err);
                    showToast("Erreur création service", "error");
                } finally {
                    setIsSaving(false);
                    setConfirmModal(prev => ({ ...prev, show: false }));
                }
            }
        });
    };

    const deleteSection = async (id) => {
        setConfirmModal({
            show: true,
            title: "Supprimer ce service ?",
            message: "Toutes les données et les blocs de cette section seront définitivement supprimés.",
            isDanger: true,
            onConfirm: async () => {
                try {
                    setIsSaving(true);
                    await pb.collection('services_sections').delete(id);
                    if (selectedSection?.id === id) setSelectedSection(null);
                    await refresh();
                    showToast("Service supprimé");
                } catch (err) {
                    console.error("Delete section failed:", err);
                    showToast("Erreur lors de la suppression du service", "error");
                } finally {
                    setIsSaving(false);
                    setConfirmModal(prev => ({ ...prev, show: false }));
                }
            }
        });
    };

    const saveSection = (e) => {
        e.preventDefault();
        setPendingSave(() => executeSaveSection);
        setShowSaveConfirm(true);
    };

    const executeSaveSection = async () => {
        if (!selectedSection) return;
        setIsSaving(true);
        try {
            await pb.collection('services_sections').update(selectedSection.id, {
                ...editData,
            });
            await refresh();
            showToast("Service sauvegardé !");
        } catch (err) {
            handleSaveError(err);
        } finally {
            setIsSaving(false);
            setIsDirty(false);
            setShowSaveConfirm(false);
        }
    };

    const cancelChanges = (target) => {
        setConfirmModal({
            show: true,
            title: "Annuler les modifications ?",
            message: "Êtes-vous sûr de vouloir ignorer tous vos changements non enregistrés ? Cette action est irréversible.",
            isDanger: true,
            onConfirm: () => {
                if (target === 'hero') {
                    if (contextHero) {
                        setHeroData({
                            title: contextHero.title || 'Services',
                            subtitle: contextHero.subtitle || '',
                            slider_title: contextHero.slider_title || '',
                            slider_subtitle: contextHero.slider_subtitle || '',
                            buttons: contextHero.buttons || []
                        });
                    }
                    setIsDirty(false);
                } else if (target === 'section' && selectedSection) {
                    handleSelectSection(selectedSection);
                } else if (target === 'category' && selectedCategory) {
                    handleSelectCategory(selectedCategory);
                }
                setConfirmModal(prev => ({ ...prev, show: false }));
            }
        });
    };


    if (contextLoading) return <ServicesLoader />;

    return (
        <section className="section">
            <div className="section-inner">
                <div className="section-header">
                    <h1 className="admin-title-gradient">Gestion des Services</h1>
                </div>

                <div style={{ display: 'flex', flexDirection: 'column', gap: '2rem' }}>
                    {/* 0. HERO SECTION CONFIG */}
                    <div className="card" style={{ borderLeft: '4px solid var(--primary, #0ea5e9)' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem', borderBottom: '1px solid var(--border-subtle)', paddingBottom: '0.5rem' }}>
                            <h3 style={{ margin: 0 }}>0. En-tête de la page (Hero)</h3>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                                {isDirty && (
                                    <>
                                        <button type="button" className="btn btn-cancel" onClick={() => cancelChanges('hero')}>↺ Annuler les modifications</button>
                                        <span className="dirty-badge">● Modifié</span>
                                    </>
                                )}
                                <button type="button" className={`btn btn-sm btn-primary ${isDirty ? 'btn-save-dirty' : ''}`} onClick={saveHero} disabled={isSaving}>Enregistrer l'en-tête</button>
                            </div>
                        </div>

                        <div style={{ display: 'grid', gridTemplateColumns: 'minmax(250px, 1fr) 1fr', gap: '1rem', marginBottom: '1rem' }}>
                            <div className="form-group">
                                <label style={{ color: 'var(--primary)' }}>Titre SLIDER (Haut de page)</label>
                                <input type="text" value={heroData.slider_title || ''} onChange={e => { setHeroData({ ...heroData, slider_title: e.target.value }); setIsDirty(true); }} placeholder="ex: Services" />
                            </div>
                            <div className="form-group">
                                <label style={{ color: 'var(--primary)' }}>Sous-titre SLIDER</label>
                                <textarea rows="2" value={heroData.slider_subtitle || ''} onChange={e => { setHeroData({ ...heroData, slider_subtitle: e.target.value }); setIsDirty(true); }} placeholder="ex: Un accompagnement..." />
                            </div>

                            <div className="form-group" style={{ borderTop: '1px dashed var(--border-subtle)', paddingTop: '1rem' }}>
                                <label>Titre CONTENU (Corps de page)</label>
                                <input type="text" value={heroData.title} onChange={e => { setHeroData({ ...heroData, title: e.target.value }); setIsDirty(true); }} />
                            </div>
                            <div className="form-group" style={{ borderTop: '1px dashed var(--border-subtle)', paddingTop: '1rem' }}>
                                <label>Sous-titre CONTENU</label>
                                <textarea rows="2" value={heroData.subtitle} onChange={e => { setHeroData({ ...heroData, subtitle: e.target.value }); setIsDirty(true); }} />
                            </div>
                        </div>

                        <div className="form-group">
                            <label style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.5rem' }}>
                                <span>Boutons d'action</span>
                                <button type="button" className="btn btn-xs btn-outline" onClick={addButton}>+ Ajouter un bouton</button>
                            </label>
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                                {heroData.buttons.length === 0 && <span style={{ color: 'var(--text-muted)', fontStyle: 'italic', fontSize: '0.9rem' }}>Aucun bouton.</span>}
                                {heroData.buttons.map((btn, idx) => (
                                    <div key={idx} style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                                        <input placeholder="Libellé" value={btn.label} onChange={e => updateButton(idx, 'label', e.target.value)} style={{ flex: 1 }} />
                                        <input placeholder="Lien (URL)" value={btn.url} onChange={e => updateButton(idx, 'url', e.target.value)} style={{ flex: 1 }} />
                                        <select value={btn.style} onChange={e => updateButton(idx, 'style', e.target.value)} style={{ width: '150px' }}>
                                            <option value="btn-secondary">Blanc (Secondaire)</option>
                                            <option value="btn-primary">Bleu (Primaire)</option>
                                        </select>
                                        <button type="button" className="btn-icon delete" onClick={() => removeButton(idx)} title="Supprimer">🗑️</button>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>

                    {/* 1. STRUCTURE */}
                    <div className="card">
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem', borderBottom: '1px solid var(--admin-border)', paddingBottom: '0.75rem' }}>
                            <div className="header-title-group">
                                <h3 style={{ margin: 0 }}>1. Structure du Sommaire</h3>
                                <p style={{ margin: 0, fontSize: '0.85rem', color: 'var(--admin-text-muted)' }}>Organisez vos groupes et vos services.</p>
                            </div>
                            <div className="actions-group">
                                <button type="button" className="btn btn-sm btn-outline" onClick={() => setShowManageCatsModal(true)}>📂 Gérer les groupes</button>
                                <button type="button" className="btn btn-sm btn-primary" onClick={createCategory}>+ Ajouter un groupe</button>
                            </div>
                        </div>

                        <div className="structure-container">
                            <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={onCategoryDragEnd}>
                                <SortableContext items={categories.map(c => c.id)} strategy={verticalListSortingStrategy}>
                                    <div style={{ display: 'flex', flexDirection: 'column' }}>
                                        {categories.map((cat, catIdx) => (
                                            <SortableCategory
                                                key={cat.id}
                                                cat={cat}
                                                catIdx={catIdx}
                                                categoriesCount={categories.length}
                                                categories={categories}
                                                sections={sections}
                                                selectedCategory={selectedCategory}
                                                handleSelectCategory={handleSelectCategory}
                                                moveCategory={() => { }}
                                                createSection={createSection}
                                                deleteCategory={deleteCategory}
                                                selectedSection={selectedSection}
                                                handleSelectSection={handleSelectSection}
                                                deleteSection={deleteSection}
                                                moveSection={() => { }}
                                                onSectionDragEnd={onSectionDragEnd}
                                                sensors={sensors}
                                                confirmNavigation={confirmNavigation}
                                                toggleSectionStatus={toggleSectionStatus}
                                            />
                                        ))}
                                    </div>
                                </SortableContext>
                            </DndContext>

                            {categories.length === 0 && (
                                <div className="empty-state">
                                    <p>Commencez par créer un groupe pour y ajouter des services.</p>
                                </div>
                            )}
                        </div>
                    </div>

                    {/* 2. EDITOR */}
                    <div id="admin-editor-area" style={{ minHeight: '400px' }}>

                        {/* CASE 1: EDITING SECTION */}
                        {selectedSection && (
                            <form onSubmit={saveSection} className="card" style={{ borderTop: '4px solid var(--admin-primary)', position: 'relative' }}>
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem', borderBottom: '1px solid var(--admin-border)', paddingBottom: '1rem' }}>
                                    <div className="header-title-group">
                                        <h3 style={{ margin: 0 }}>⚙️ Configuration du service</h3>
                                        <p className="text-muted" style={{ fontSize: '0.9rem' }}>Modifiez les informations générales et le contenu ci-dessous.</p>
                                    </div>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                                        {isDirty && (
                                            <>
                                                <button type="button" className="btn btn-cancel" onClick={() => cancelChanges('section')}>↺ Annuler les modifications</button>
                                                <span className="dirty-badge">● Changements non sauvés</span>
                                            </>
                                        )}
                                        <button type="submit" className={`btn btn-primary ${isDirty ? 'btn-save-dirty' : ''}`} disabled={isSaving} style={{ padding: '0.75rem 1.5rem' }}>
                                            {isSaving ? 'Enregistrement...' : 'Sauvegarder les modifications'}
                                        </button>
                                    </div>
                                </div>

                                <div style={{ display: 'grid', gridTemplateColumns: 'minmax(300px, 1.5fr) 1fr', gap: '2rem', marginBottom: '2rem' }}>
                                    <div className="form-main">
                                        <div className="form-group">
                                            <label>Nom du service (Affiché dans le sommaire)</label>
                                            <input
                                                type="text"
                                                value={editData.title}
                                                onChange={e => {
                                                    const val = e.target.value;
                                                    setEditData(prev => ({ ...prev, title: val, slug: prev.slug === slugify(prev.title) ? slugify(val) : prev.slug }));
                                                    setIsDirty(true);
                                                }}
                                                placeholder="ex: Hébergement Mutualisé"
                                                required
                                            />
                                        </div>

                                        <div className="form-group">
                                            <label>Slug (URL) <span className="text-muted" style={{ fontWeight: 400, fontSize: '0.8rem' }}>(ex: versionnet.fr/services#{editData.slug})</span></label>
                                            <div style={{ display: 'flex', gap: '0.5rem' }}>
                                                <span style={{ alignSelf: 'center', color: 'var(--admin-text-muted)' }}>#</span>
                                                <input
                                                    type="text"
                                                    value={editData.slug}
                                                    onChange={e => { setEditData({ ...editData, slug: slugify(e.target.value) }); setIsDirty(true); }}
                                                    style={{ flex: 1 }}
                                                />
                                            </div>
                                        </div>
                                    </div>

                                    <div className="form-sidebar" style={{ background: 'var(--admin-hover-bg)', padding: '1.25rem', borderRadius: '12px', border: '1px solid var(--admin-border)' }}>
                                        <div className="form-group" style={{ marginBottom: '1.5rem', padding: '1rem', background: 'var(--admin-card-bg)', borderRadius: '8px', border: '1px solid var(--admin-border)' }}>
                                            <label style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', cursor: 'pointer', margin: 0 }}>
                                                <input
                                                    type="checkbox"
                                                    checked={editData.is_published}
                                                    onChange={e => { setEditData({ ...editData, is_published: e.target.checked }); setIsDirty(true); }}
                                                    style={{ width: '18px', height: '18px' }}
                                                />
                                                <span style={{ fontWeight: 600 }}>Publier cette section</span>
                                            </label>
                                            <p style={{ margin: '0.5rem 0 0 2rem', fontSize: '0.8rem', color: 'var(--admin-text-muted)' }}>
                                                Si décoché, cette section sera masquée sur le site public.
                                            </p>
                                        </div>

                                        <div className="form-group">
                                            <label>Groupe parent</label>
                                            <select
                                                value={editData.category}
                                                onChange={e => { setEditData({ ...editData, category: e.target.value }); setIsDirty(true); }}
                                                required
                                            >
                                                <option value="" disabled>Choisir un groupe...</option>
                                                {categories.map(cat => (
                                                    <option key={cat.id} value={cat.id}>{cat.title}</option>
                                                ))}
                                            </select>
                                        </div>

                                        <div style={{ marginTop: '1.5rem', padding: '1rem', background: 'var(--admin-card-bg)', borderRadius: '8px', border: '1px solid var(--admin-border)', fontSize: '0.85rem' }}>
                                            <h4 style={{ margin: '0 0 0.5rem 0', fontSize: '0.9rem' }}>💡 Conseils</h4>
                                            <p style={{ margin: 0, opacity: 0.8 }}>Le slug est utilisé pour les liens d'ancrage. Évitez de le changer si la page est déjà indexée.</p>
                                        </div>
                                    </div>
                                </div>

                                <div className="form-group" style={{ borderTop: '1px solid var(--admin-border)', paddingTop: '2rem' }}>
                                    <label style={{ fontSize: '1.1rem', marginBottom: '1rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                                        <span>🧩 Blocs de contenu</span>
                                        <span style={{ fontWeight: 400, fontSize: '0.85rem', color: 'var(--admin-text-muted)' }}>(Construction visuelle)</span>
                                    </label>
                                    <BlockListEditor
                                        blocks={editData.blocks}
                                        onChange={(newBlocks) => { setEditData({ ...editData, blocks: newBlocks }); setIsDirty(true); }}
                                    />
                                </div>
                            </form>
                        )}

                        {/* CASE 2: EDITING CATEGORY */}
                        {selectedCategory && (
                            <form onSubmit={saveCategory} className="card" style={{ borderTop: '4px solid var(--admin-secondary, #6366f1)', position: 'relative' }}>
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem', borderBottom: '1px solid var(--admin-border)', paddingBottom: '1rem' }}>
                                    <div className="header-title-group">
                                        <h3 style={{ margin: 0 }}>📁 Configuration du groupe</h3>
                                        <p className="text-muted" style={{ fontSize: '0.9rem' }}>Modifiez le titre et l'identifiant du groupe.</p>
                                    </div>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                                        {isDirty && (
                                            <>
                                                <button type="button" className="btn btn-cancel" onClick={() => cancelChanges('category')}>↺ Annuler les modifications</button>
                                                <span className="dirty-badge">● Modifié</span>
                                            </>
                                        )}
                                        <button type="submit" className={`btn btn-secondary ${isDirty ? 'btn-save-dirty' : ''}`} disabled={isSaving}>Enregistrer le groupe</button>
                                    </div>
                                </div>

                                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '2rem' }}>
                                    <div className="form-group">
                                        <label>Nom du groupe (Titre SEO)</label>
                                        <input
                                            type="text"
                                            value={catEditData.title}
                                            onChange={e => {
                                                const val = e.target.value;
                                                setCatEditData(prev => ({ ...prev, title: val, slug: prev.slug === slugify(prev.title) ? slugify(val) : prev.slug }));
                                                setIsDirty(true);
                                            }}
                                            required
                                        />
                                    </div>
                                    <div className="form-group">
                                        <label>Slug de groupe</label>
                                        <input
                                            type="text"
                                            value={catEditData.slug}
                                            onChange={e => { setCatEditData({ ...catEditData, slug: slugify(e.target.value) }); setIsDirty(true); }}
                                            required
                                        />
                                    </div>
                                </div>
                            </form>
                        )}

                        {!selectedSection && !selectedCategory && (
                            <div className="card empty-state" style={{ height: '300px', display: 'grid', placeItems: 'center', opacity: 0.7 }}>
                                <div style={{ textAlign: 'center' }}>
                                    <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>☝️</div>
                                    <p>Sélectionnez une catégorie ou un service dans la structure <br /> pour commencer l'édition.</p>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {/* MODALS & TOASTS */}
            {
                toast.show && (
                    <div className={`admin-toast ${toast.type}`}>
                        {toast.message}
                    </div>
                )
            }

            {
                showNewCatModal && (
                    <div className="admin-modal-overlay">
                        <div className="admin-modal scale-in" style={{ maxWidth: '450px' }}>
                            <div className="admin-modal-header">
                                <h3 style={{ color: 'var(--admin-primary)' }}>📁 Nouvelle catégorie</h3>
                            </div>
                            <div className="admin-modal-body">
                                <div className="admin-input-group">
                                    <label>Titre de la catégorie</label>
                                    <input
                                        type="text"
                                        value={newCatTitle}
                                        onChange={e => setNewCatTitle(e.target.value)}
                                        placeholder="Ex: Offres d'alternance"
                                        autoFocus
                                        onKeyDown={e => e.key === 'Enter' && handleConfirmCreateCategory()}
                                        style={{ width: '100%', padding: '0.8rem', borderRadius: '8px', border: '1px solid var(--admin-border)', background: 'var(--admin-input-bg)', color: 'var(--admin-text)' }}
                                    />
                                </div>
                            </div>
                            <div className="admin-modal-footer">
                                <button type="button" className="btn btn-ghost" onClick={() => setShowNewCatModal(false)} disabled={isSaving}>Annuler</button>
                                <button type="button" className="btn btn-primary" onClick={handleConfirmCreateCategory} disabled={!newCatTitle.trim() || isSaving}>
                                    {isSaving ? (
                                        <span style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                                            <svg className="spinner" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" width="16" height="16" style={{ animation: 'spin 1s linear infinite' }}>
                                                <circle cx="12" cy="12" r="10" strokeOpacity="0.25" />
                                                <path d="M12 2C6.47715 2 2 6.47715 2 12" />
                                            </svg>
                                            Création en cours...
                                        </span>
                                    ) : 'Créer la catégorie'}
                                </button>
                            </div>
                        </div>
                    </div>
                )
            }

            {
                showManageCatsModal && (
                    <div className="admin-modal-overlay">
                        <div className="admin-modal scale-in" style={{ maxWidth: '600px' }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
                                <h3 style={{ margin: 0 }}>📂 Réorganiser les groupes</h3>
                                <button type="button" className="btn-icon" onClick={() => setShowManageCatsModal(false)}>×</button>
                            </div>
                            <p className="text-muted" style={{ marginBottom: '1rem' }}>Glissez-déposez pour réorganiser l'ordre d'affichage dans le menu.</p>

                            <div style={{ maxHeight: '400px', overflowY: 'auto', padding: '0.5rem' }}>
                                <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={onCategoryDragEnd}>
                                    <SortableContext items={categories.map(c => c.id)} strategy={verticalListSortingStrategy}>
                                        {categories.map((cat, idx) => (
                                            <SortableModalCategory key={cat.id} cat={cat} index={idx} />
                                        ))}
                                    </SortableContext>
                                </DndContext>
                            </div>

                            <div className="admin-modal-footer">
                                <button type="button" className="btn btn-primary" style={{ width: '100%' }} onClick={() => setShowManageCatsModal(false)}>Terminer</button>
                            </div>
                        </div>
                    </div>
                )
            }

            {
                confirmModal.show && (
                    <div className="admin-modal-overlay">
                        <div className="admin-modal scale-in">
                            <h3>{confirmModal.title}</h3>
                            <p>{confirmModal.message}</p>
                            <div className="admin-modal-footer">
                                <button type="button" className="btn btn-ghost" onClick={() => setConfirmModal(prev => ({ ...prev, show: false }))}>Annuler</button>
                                <button type="button" className={`btn ${confirmModal.isDanger ? 'btn-danger' : 'btn-primary'}`} onClick={confirmModal.onConfirm}>Confirmer</button>
                            </div>
                        </div>
                    </div>
                )
            }

            {
                showSaveConfirm && (
                    <div className="admin-modal-overlay" style={{ zIndex: 100000 }}>
                        <div className="admin-modal scale-in" style={{ textAlign: 'center', padding: '2.5rem' }}>
                            <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>💾</div>
                            <h3>Enregistrer les changements ?</h3>
                            <p className="text-muted">Ces modifications seront appliquées immédiatement sur le site public.</p>
                            <div className="admin-modal-footer" style={{ justifyContent: 'center', marginTop: '2rem', gap: '1rem' }}>
                                <button type="button" className="btn btn-ghost" onClick={() => setShowSaveConfirm(false)}>Continuer l'édition</button>
                                <button type="button" className="btn btn-primary" style={{ padding: '0.8rem 2rem' }} onClick={pendingSave} disabled={isSaving}>
                                    {isSaving ? 'Enregistrement...' : 'Confirmer et Publier'}
                                </button>
                            </div>
                        </div>
                    </div>
                )
            }
        </section >
    );
}
